import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { calculateMultiSegmentForecast, updateMultiSegmentForecast } from '../helpers/calculateForecast';

import { deleteGroupWellApi, getGroupWellParamsApi, getGroupWellsApi, saveGroupWellApi } from '../api/groupWellAPI';
import { calculateWaterfloodForecast } from '../helpers/calculateWaterfloodForecast';

const initialState = {
  byId: {
  },
  allIds: [],
  currentGroupWellId: ""
};

export const getGroupWells = createAsyncThunk(
  'groupWells/getGroupWells',
  async (user, { rejectWithValue }) => {
    try {
      const groupWells = await getGroupWellsApi(user);
      return groupWells;
    } catch (error) {
      return rejectWithValue(user);
    };
  }
);

export const deleteGroupWell = createAsyncThunk(
  'groupWells/deleteGroupWell',
  async (payload, { getState }) => {
    try {

      const state = getState();
      const email = state?.user?.value?.email;

      await deleteGroupWellApi(payload.groupWellId, email);

      return { groupWellId: payload.groupWellId };
    } catch (error) {
      return false;
    };
  }
);

export const getGroupWellParams = createAsyncThunk(
  'groupWells/getGroupWellParams',
  async (payload, { rejectWithValue }) => {
    try {
      let well = {...payload.well};
      if (well) {
        const params = await getGroupWellParamsApi(well);
        well.params = params;

        const fluids = Object.keys(well.params);
        for (let fluid of fluids) {
          if (Object.keys(well.params?.[fluid])?.length > 0) {

            const {newFluidParams, forecast, eur} = await calculateMultiSegmentForecast(well, fluid);
           
            well.params = {...well.params, [fluid]: newFluidParams};
            let wellForecast = {...well.forecast};
            wellForecast.byFluid = {...wellForecast.byFluid, [fluid]: forecast};
            well.forecast = wellForecast;
            let wellEur = {...well.eur};
            wellEur.byFluid = {...wellEur.byFluid, [fluid]: eur};
            well.eur = wellEur;
          };
        };
        return well;
      };

    } catch (error) {
      return rejectWithValue(payload?.well?.id);
    };
  }
);

export const getGroupWellForecast = createAsyncThunk(
  'groupWells/getGroupWellForecast',
  async (groupWell, { rejectWithValue }) => {
    try {
      let well = {...groupWell};
      if (well && well.production && well.production.fluids) {
        const fluids = well.production.fluids; // filter out water
        // const fluids = well.production.fluids.filter(f => f !== 'water'); // filter out water
        for (let fluid of fluids) {
          if (well.params?.[fluid] && Object.keys(well.params?.[fluid])?.length > 0) {

            const {newFluidParams, forecast, eur} = await calculateMultiSegmentForecast(well, fluid);
           
            well.params = {...well.params, [fluid]: newFluidParams};
            if (fluid === 'oil' && well.params.oil) {
              well.params.oil.useWaterflood = false;
            };
            let wellForecast = {...well.forecast};
            wellForecast.byFluid = {...wellForecast.byFluid, [fluid]: forecast};
            well.forecast = wellForecast;
            let wellEur = {...well.eur};
            wellEur.byFluid = {...wellEur.byFluid, [fluid]: eur};
            well.eur = wellEur;
          };
        };
        return well;
      };

    } catch (error) {
      console.log(error);
      return rejectWithValue(groupWell?.id);
    };
  }
);

export const getWaterfloodForecastGW = createAsyncThunk(
  'groupWells/getWaterfloodForecastGW',
  async (well, { rejectWithValue }) => {
    try {
      const production = well.production;
      const params = well.params.waterflood;

      if (production && params) {
        
        const { oilForecast, eur } = await calculateWaterfloodForecast(production, params);
        
        return {...well, forecast: {byFluid: { oil: oilForecast }}, eur: {byFluid: { oil: eur }}, params: {...well.params, waterflood: params }};
      };

    } catch (error) {
      return rejectWithValue(well?.uwi);
    };
  }
);

export const updateGroupWellForecast = createAsyncThunk(
  'groupWells/updateGroupWellForecast',
  async (payload, { rejectWithValue }) => {
    try {
      let well = {...payload.well};
      const fluid = payload.fluid;
      const param = payload.param;
      const value = payload.value;
      const { newFluidParams, forecast, eur } = await updateMultiSegmentForecast(well, fluid, param, value);
      
      well.params = {...well.params, [fluid]: newFluidParams};
      if (param === 'cutoff') well.params.cutoff = value;
      well.forecast = {...well.forecast, byFluid: {...well.forecast.byFluid, [fluid]: forecast}};
      well.eur = {...well.eur, byFluid: {...well.eur.byFluid, [fluid]: eur}};

      return well;
    } catch (error) {
      return rejectWithValue(payload?.well?.id);
    };
  }
);

export const saveGroupWell = createAsyncThunk(
  'groupWells/saveGroupWell',
  async (groupWell, { rejectWithValue, getState }) => {
    try {

      const state = getState();
      const email = state?.user?.value?.email;

      const { success, date } = await saveGroupWellApi(groupWell, email);

      return { groupWell: groupWell, success: success, date: date };
    } catch (err) {
      return rejectWithValue([], err);
    };
  }
);

export const saveGroupWellParams = createAsyncThunk(
  'groupWells/saveGroupWellParams',
  async (groupWell, { rejectWithValue }) => {
    try {

      const { success, date } = await saveGroupWellApi(groupWell);

      return { groupWell: groupWell, success: success, date: date };
    } catch (err) {
      return rejectWithValue([], err);
    };
  }
);

export const addGroupWell = createAsyncThunk(
  'groupWells/addGroupWell',
  async (groupWell, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const email = state?.user?.value?.email;

      const { success, date } = await saveGroupWellApi(groupWell, email);

      return { groupWell: groupWell, success: success, date: date };
    } catch (err) {
      return rejectWithValue([], err);
    };
  }
);

export const groupWellSlice = createSlice({
  name: 'groupWells',
  initialState,
  reducers: {
    toggleCheckGroupWell: (state, action) => {
      try {
        const groupWellId = action.payload.groupWellId;
        if (!state?.byId?.[groupWellId]) return;
  
        if (!state?.byId?.[groupWellId].checked) {
          state.byId[groupWellId].checked = true;      
        } else {
          state.byId[groupWellId].checked = false;
        };    
      } catch (error) {
        return;
      };
    },
    toggleCheckAllGroupWells: (state, action) => {
      try {
        const groupWellIds = action.payload.groupWellIds;
        groupWellIds?.forEach(id => {
          state.byId[id].checked = true;
        });
      } catch (error) {
        return;
      };
    },
    toggleUncheckAllGroupWells: (state, action) => {
      try {
        const groupWellIds = action.payload.groupWellIds;
        groupWellIds?.forEach(id => {
          state.byId[id].checked = false;
        });
      } catch (error) {
        return;
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addGroupWell.fulfilled, (state, action) => {
      try {
        const id = action.payload.groupWell.id;
        state.byId[id] = action.payload.groupWell;
        if (state.allIds.indexOf(id) === -1) state.allIds.push(id);
        state.currentGroupWellId = id;
      } catch (error) {
        return;
      };
    })
    builder.addCase(getGroupWellForecast.fulfilled, (state, action) => {
      try {
        const id = action.payload?.id;
        state.byId[id] = action.payload;
        state.byId[id].checked = true;
        if (state.allIds.indexOf(id) === -1) state.allIds.push(id);
        state.currentGroupWellId = id;
        
      } catch (error) {
        return;  
      };
    })
    builder.addCase(getWaterfloodForecastGW.fulfilled, (state, action) => {
      try {
        const well = action.payload;
        const id = well.id;
        state.byId[id] = {...well, checked: true};
      } catch (error) {
        console.log(error);
        return;  
      };
    })
    builder.addCase(getGroupWellParams.fulfilled, (state, action) => {
      try {
        const id = action.payload.id;
        state.byId[id] = action.payload;
        if (state.allIds.indexOf(id) === -1) state.allIds.push(id);
        state.currentGroupWellId = id;
      } catch (error) {
        console.log(error);
        return;
      };
    })
    builder.addCase(updateGroupWellForecast.fulfilled, (state, action) => {
      try {
        const id = action.payload.id;
        state.byId[id] = action.payload;
        if (state.allIds.indexOf(id) === -1) state.allIds.push(id);
        state.currentGroupWellId = id;
      } catch (error) {
        return;
      };
    })
    builder.addCase(getGroupWells.fulfilled, (state, action) => {
      try {
        const groupWells = action.payload;
        if (groupWells?.length > 0) {
          for (let groupWell of groupWells) {
            state.byId[groupWell.id] = groupWell;
            if (state.allIds.indexOf(groupWell.id) === -1) state.allIds.push(groupWell.id);
            state.currentGroupWellId = groupWell.id;
          };
        };
      } catch (error) {
        return;
      };
    })
    builder.addCase(saveGroupWellParams.fulfilled, (state, action) => {
      try {
        const groupWell = action.payload.groupWell;
        state.byId[groupWell.id] = groupWell;
      } catch (error) {
        return;
      };
    })
    builder.addCase(deleteGroupWell.fulfilled, (state, action) => {
      try {
        const groupWellId = action.payload.groupWellId;
        delete state.byId[groupWellId];
        state.allIds = state.allIds.filter(id => id !== groupWellId);
      } catch (error) {
        return;
      };
    })
  },
});

export const selectGroupWells = (state) => state.groupWells;

export const {
  // addGroupWell,
  toggleCheckGroupWell,
  toggleCheckAllGroupWells,
  toggleUncheckAllGroupWells
} = groupWellSlice.actions;

export default groupWellSlice.reducer;